import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, TextField, InputAdornment, Tab, Tabs, LinearProgress,
  IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel, Link, FormControlLabel, Checkbox, ListSubheader } from '@mui/material';
import Layout from '../components/Layout';
import api, { invoiceService, projectService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FileUpload from '../components/FileUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import FolderIcon from '@mui/icons-material/Folder';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const statusConfig = {
  submitted: { color: '#fff3e0', text: '#e65100', bg: '#ff9800', label: 'Pending Review' },
  approved:  { color: '#e3f2fd', text: '#1565c0', bg: '#1976d2', label: 'Approved' },
  paid:      { color: '#e8f5e9', text: '#2e7d32', bg: '#43a047', label: 'Paid' },
};

export default function Invoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [smartFilter, setSmartFilter] = useState('all'); // 'all' | 'addr::<address>' | 'cust::<customer>'
  const [vendorFilter, setVendorFilter] = useState('all');
  const [paidFilter, setPaidFilter] = useState('all'); // all | unpaid | paid | reconciled
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reminderDialog, setReminderDialog] = useState({ open: false, invoice: null });
  const [reminderMsg, setReminderMsg] = useState('');
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const [reminderOverrideOptout, setReminderOverrideOptout] = useState(false);
  const [payDialog, setPayDialog] = useState({ open: false, invoice: null });
  const [payForm, setPayForm] = useState({ payment_method: 'check', payment_reference: '', attachments: [] });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInvoices();
    if (user?.role === 'subcontractor') setPaidFilter('unpaid');
    if (user?.role === 'homeowner') fetchProjects();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await invoiceService.getAll();
      setInvoices(res.data);
    } catch (err) {}
  };

  const fetchProjects = async () => {
    try {
      const res = await projectService.getAll();
      setProjects(res.data);
    } catch (err) {}
  };

  const handleApprove = async (invoice) => {
    setLoading(true);
    try {
      await invoiceService.approve(invoice.id);
      setInvoices(invoices.map(i => i.id === invoice.id ? { ...i, status: 'approved' } : i));
      setDetailOpen(false);
      showAlert('Invoice approved successfully!', 'success');
    } catch (err) {
      showAlert('Failed to approve invoice', 'error');
    }
    setLoading(false);
  };

  const openReminderDialog = (inv) => {
    setReminderDialog({ open: true, invoice: inv });
    setReminderMsg('');
    setReminderError('');
    setReminderOverrideOptout(false);
  };

  const sendInvoiceReminder = async () => {
    if (!reminderDialog.invoice) return;
    setReminderSending(true);
    setReminderError('');
    try {
      const body = {};
      if (reminderMsg.trim().length > 0) body.custom_message = reminderMsg.trim();
      if (reminderOverrideOptout) body.override_optout = true;
      await api.post(`/invoices/${reminderDialog.invoice.id}/send-reminder`, body);
      setReminderDialog({ open: false, invoice: null });
      alert('Reminder sent. You were cc\'d.');
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 403 && data.opted_out) {
        setReminderError('This customer is on your opt-out list. Toggle "Send anyway" below to override.');
        setReminderOverrideOptout(false);
      } else if (err.response?.status === 429) {
        const when = data.last_sent_at ? new Date(data.last_sent_at).toLocaleString() : 'in the last 24 hours';
        setReminderError(`Already sent ${when}. Try again later.`);
      } else {
        setReminderError(data.error || data.message || 'Failed to send reminder');
      }
    } finally {
      setReminderSending(false);
    }
  };

  const handleReconcile = async (inv) => {
    try {
      await api.put(`/invoices/${inv.id}/status`, { status: 'closed' });
      setInvoices(invoices.map(i => i.id === inv.id ? { ...i, status: 'closed' } : i));
    } catch (e) {
      alert('Failed to mark reconciled');
    }
  };

  const handleMarkPaid = async () => {
    setLoading(true);
    try {
      await invoiceService.markPaid(payDialog.invoice.id, payForm);
      setInvoices(invoices.map(i => i.id === payDialog.invoice.id ? { ...i, status: 'paid', paid_at: new Date().toISOString(), ...payForm } : i));
      setPayDialog({ open: false, invoice: null });
      setDetailOpen(false);
      showAlert('Invoice marked as paid!', 'success');
    } catch (err) {
      showAlert('Failed to mark as paid', 'error');
    }
    setLoading(false);
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  };

  // Vendor list for dropdown
  const dueDate30 = (inv) => {
    const base = inv.invoice_date || inv.submitted_at;
    if (!base) return '—';
    const d = new Date(new Date(base).getTime() + 30 * 24 * 60 * 60 * 1000);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
  };

  const isSub = user?.role === 'subcontractor';

  const dueDateObj = (inv) => {
    const base = inv.invoice_date || inv.submitted_at;
    if (!base) return null;
    const d = new Date(new Date(base).getTime() + 30 * 24 * 60 * 60 * 1000);
    return isNaN(d.getTime()) ? null : d;
  };

  const agingInfo = (inv) => {
    if (inv.status === 'paid') return { label: 'Paid', color: '#e8f5e9', text: '#2e7d32' };
    if (inv.status === 'closed') return { label: 'Reconciled', color: '#ede7f6', text: '#5e35b1' };
    const due = dueDateObj(inv);
    if (!due) return { label: '—', color: '#f5f6fa', text: '#999' };
    const days = Math.ceil((due.getTime() - Date.now()) / 86400000);
    if (days < 0) return { label: `Overdue ${Math.abs(days)}d`, color: '#ffebee', text: '#c62828' };
    if (days <= 7) return { label: `Due ${days}d`, color: '#fff3e0', text: '#e65100' };
    return { label: 'Current', color: '#e8f5e9', text: '#2e7d32' };
  };

  const vendorOptions = Array.from(new Set(invoices.map(i => i.vendor_name || (i.first_name ? `${i.first_name} ${i.last_name}` : null)).filter(Boolean))).sort();

  // Filter invoices
  // Smart filter narrows to a single address or customer; cards + table both honor it
  const matchesSmart = (inv) => {
    if (smartFilter === 'all') return true;
    const [kind, val] = smartFilter.split('::');
    if (kind === 'addr') return (inv.project_address || inv.project_name) === val;
    if (kind === 'cust') return (inv.customer_name || '') === val;
    return true;
  };
  const scoped = invoices.filter(matchesSmart);

  const filtered = scoped.filter(inv => {
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    const vendor = inv.vendor_name || (inv.first_name ? `${inv.first_name} ${inv.last_name}` : '');
    const matchesVendor = isSub ? true : (vendorFilter === 'all' || vendor === vendorFilter);
    const matchesProject = !isSub || selectedProject === 'all' || inv.project_address === selectedProject || inv.project_name === selectedProject || inv.project_id === selectedProject;
    const isPaid = inv.status === 'paid';
    const isReconciled = inv.status === 'closed';
    let matchesPaid = true;
    if (paidFilter === 'paid') matchesPaid = isPaid;
    else if (paidFilter === 'reconciled') matchesPaid = isReconciled;
    else if (paidFilter === 'unpaid') matchesPaid = !isPaid && !isReconciled;
    const q = (search || '').toLowerCase();
    const matchesSearch = !q ||
      (inv.invoice_number || '').toLowerCase().includes(q) ||
      vendor.toLowerCase().includes(q) ||
      (inv.notes || '').toLowerCase().includes(q);
    return matchesStatus && matchesVendor && matchesProject && matchesPaid && matchesSearch;
  }).sort((a, b) => {
    // Unpaid first, then paid/reconciled at bottom; within group oldest submitted first
    const aPaid = (a.status === 'paid' || a.status === 'closed') ? 1 : 0;
    const bPaid = (b.status === 'paid' || b.status === 'closed') ? 1 : 0;
    if (aPaid !== bPaid) return aPaid - bPaid;
    return new Date(a.submitted_at || 0) - new Date(b.submitted_at || 0);
  });

  // Summary stats
  const totalPending = scoped.filter(i => i.status === 'submitted' || i.status === 'approved')
    .reduce((sum, i) => sum + Number(i.amount), 0);
  const totalPaid = scoped.filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + Number(i.amount), 0);
  const totalAmount = scoped.reduce((sum, i) => sum + Number(i.amount), 0);
  const paidPercent = totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0;
  const subOverdue = scoped.filter(i => i.status !== 'paid').reduce((s, i) => {
    const due = dueDateObj(i);
    return (due && due.getTime() < Date.now()) ? s + Number(i.amount) : s;
  }, 0);

  // Group by project
  const projectGroups = projects.reduce((acc, proj) => {
    const projInvoices = invoices.filter(inv => inv.project_id === proj.id);
    if (projInvoices.length > 0) acc[proj.id] = { project: proj, invoices: projInvoices };
    return acc;
  }, {});

  return (
    <Layout>
      {alert.show && <Alert severity={alert.severity} sx={{ mb: 3, borderRadius: 2 }}>{alert.message}</Alert>}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>Invoices</Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>{isSub ? 'Money owed to you — outstanding by project' : 'Track payments across all your projects'}</Typography>
      </Box>

      {/* Filters */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6', mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField fullWidth placeholder="Search invoices..." value={search}
                onChange={e => setSearch(e.target.value)} size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={3}>
              {isSub ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Address or Customer</InputLabel>
                  <Select value={smartFilter} onChange={e => setSmartFilter(e.target.value)} label="Address or Customer" sx={{ borderRadius: 2 }}>
                    <MenuItem value="all">All invoices</MenuItem>
                    <ListSubheader sx={{ fontWeight: 700, color: '#1a237e' }}>By Address</ListSubheader>
                    {Array.from(new Set(invoices.map(i => i.project_address || i.project_name).filter(Boolean))).sort().map(addr => (
                      <MenuItem key={'addr-'+addr} value={'addr::'+addr} sx={{ pl: 3 }}>{addr}</MenuItem>
                    ))}
                    <ListSubheader sx={{ fontWeight: 700, color: '#1a237e' }}>By Customer</ListSubheader>
                    {Array.from(new Set(invoices.map(i => i.customer_name).filter(Boolean))).sort().map(c => (
                      <MenuItem key={'cust-'+c} value={'cust::'+c} sx={{ pl: 3 }}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>Vendor</InputLabel>
                  <Select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} label="Vendor" sx={{ borderRadius: 2 }}>
                    <MenuItem value="all">All Vendors</MenuItem>
                    {vendorOptions.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[{k:'all',l:'All'},{k:'unpaid',l:'Unpaid'},{k:'paid',l:'Paid'},{k:'reconciled',l:'Reconciled'}].map(o => (
                  <Chip key={o.k} label={o.l}
                    onClick={() => setPaidFilter(o.k)}
                    sx={{ cursor: 'pointer', fontWeight: 600, px: 1,
                      bgcolor: paidFilter === o.k ? (o.k === 'paid' ? '#2e7d32' : o.k === 'unpaid' ? '#e65100' : o.k === 'reconciled' ? '#5e35b1' : '#1a237e') : '#f5f6fa',
                      color: paidFilter === o.k ? 'white' : '#666' }} />
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {isSub ? (
          <>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #1a237e, #283593)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{scoped.length}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Invoices</Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #e65100, #f57c00)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${totalAmount.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Invoice Amount</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #2e7d32, #43a047)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${totalPaid.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Collected</Typography>
                </Box>
                <PaidIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #0277bd, #0288d1)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${(totalAmount - totalPaid).toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Remaining</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #6a1b9a, #8e24aa)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${subOverdue.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Overdue</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
          </>
        ) : (
          <>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #1a237e, #283593)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{invoices.length}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Invoices</Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #e65100, #f57c00)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${totalPending.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Pending Amount</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #2e7d32, #43a047)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${totalPaid.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Paid</Typography>
                </Box>
                <PaidIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #6a1b9a, #8e24aa)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{`${paidPercent.toFixed(0)}%`}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Payment Progress</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
              <LinearProgress variant="determinate" value={paidPercent}
                sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
            </CardContent>
          </Card>
        </Grid>
          </>
        )}
      </Grid>

      {/* Project Summary (homeowner only) */}
      {user?.role === 'homeowner' && projects.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>By Project</Typography>
          <Grid container spacing={2}>
            {projects.map(proj => {
              const projInvoices = invoices.filter(inv => inv.project_id === proj.id);
              const projPaid = projInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.amount), 0);
              const projPending = projInvoices.filter(i => i.status !== 'paid').reduce((s, i) => s + Number(i.amount), 0);
              if (projInvoices.length === 0) return null;
              return (
                <Grid item xs={12} md={4} key={proj.id}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6', cursor: 'pointer',
                    bgcolor: selectedProject === proj.id ? '#e8eaf6' : 'white',
                    '&:hover': { bgcolor: '#f5f6fa' } }}
                    onClick={() => setSelectedProject(selectedProject === proj.id ? 'all' : proj.id)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <FolderIcon sx={{ color: '#1a237e' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{proj.name}</Typography>
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                            <Typography variant="caption" sx={{ color: '#2e7d32' }}>Paid</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#2e7d32' }}>${projPaid.toLocaleString()}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 2 }}>
                            <Typography variant="caption" sx={{ color: '#e65100' }}>Pending</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#e65100' }}>${projPending.toLocaleString()}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Invoice Table */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f6fa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Invoice #</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Project</TableCell>
                {!isSub && <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Contractor</TableCell>}
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Invoice Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Due Date</TableCell>
                {isSub && <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Aging</TableCell>}
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', py: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                    <ReceiptIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                    <Typography sx={{ color: '#999' }}>No invoices found</Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(inv => (
                <TableRow key={inv.id} sx={{ '&:hover': { bgcolor: '#f5f6fa' } }}>
                  <TableCell sx={{ py: 1 }}>
                    {inv.invoice_document_url ? (
                      <Link href={inv.invoice_document_url} target="_blank" rel="noopener noreferrer"
                        sx={{ fontWeight: 700, color: '#1a237e', textDecoration: 'underline', cursor: 'pointer' }}>
                        {inv.invoice_number}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }}>{inv.invoice_number}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{inv.project_address || inv.project_name || '—'}</Typography>
                    {inv.project_address && inv.project_name && <Typography variant="caption" color="text.secondary" display="block">{inv.project_name}</Typography>}
                  </TableCell>
                  {!isSub && (
                  <TableCell sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: '#1a237e', width: 24, height: 24, fontSize: 11 }}>
                        {(inv.vendor_name || inv.first_name || '?')[0]}
                      </Avatar>
                      <Typography variant="body2">{inv.vendor_name || (inv.first_name ? `${inv.first_name} ${inv.last_name}` : '—')}</Typography>
                    </Box>
                  </TableCell>
                  )}
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2">{inv.is_external ? (inv.customer_name || '—') : '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>${Number(inv.amount).toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Chip label={statusConfig[inv.status]?.label || inv.status} size="small"
                      sx={{ bgcolor: statusConfig[inv.status]?.color, color: statusConfig[inv.status]?.text, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : new Date(inv.submitted_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>{dueDate30(inv)}</Typography>
                  </TableCell>
                  {isSub && (
                  <TableCell sx={{ py: 1 }}>
                    {(() => { const a = agingInfo(inv); return (
                      <Chip label={a.label} size="small" sx={{ bgcolor: a.color, color: a.text, fontWeight: 600, fontSize: 11 }} />
                    ); })()}
                  </TableCell>
                  )}
                  <TableCell sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => { setSelectedInvoice(inv); setDetailOpen(true); }}
                          sx={{ color: '#1a237e' }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {user?.role === 'homeowner' && inv.status === 'submitted' && (
                        <Button size="small" variant="contained" onClick={() => handleApprove(inv)}
                          sx={{ bgcolor: '#1976d2', borderRadius: 1, fontSize: 11, px: 1.5 }}>
                          Approve
                        </Button>
                      )}
                      {user?.role === 'homeowner' && inv.status === 'approved' && (
                        <Button size="small" variant="contained"
                          onClick={() => setPayDialog({ open: true, invoice: inv })}
                          sx={{ bgcolor: '#2e7d32', borderRadius: 1, fontSize: 11, px: 1.5 }}>
                          Mark Paid
                        </Button>
                      )}
                      {isSub && (inv.status === 'submitted' || inv.status === 'approved') && (
                        <Tooltip title="Send reminder to customer">
                          <IconButton size="small" onClick={() => openReminderDialog(inv)}
                            sx={{ color: '#ef9f27' }}>
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                                            {isSub && inv.status === 'paid' && (
                        <Button size="small" variant="contained"
                          onClick={() => handleReconcile(inv)}
                          sx={{ bgcolor: '#5e35b1', borderRadius: 1, fontSize: 11, px: 1.5 }}>
                          Mark Reconciled
                        </Button>
                      )}
                      {isSub && inv.status === 'closed' && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: '#ede7f6', borderRadius: 1, fontSize: 11, color: '#5e35b1', fontWeight: 600 }}>
                          ✓ Reconciled
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Invoice Detail Modal */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        {selectedInvoice && (
          <>
            <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedInvoice.invoice_number}</Typography>
                <Chip label={statusConfig[selectedInvoice.status]?.label} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {/* ===== INVOICE DETAILS ===== */}
              <Typography variant="overline" sx={{ color: '#1a237e', fontWeight: 700, letterSpacing: 1 }}>Invoice Details</Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999' }}>Amount</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>
                    ${Number(selectedInvoice.amount).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999' }}>Due Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedInvoice.due_date
                      ? new Date(selectedInvoice.due_date).toLocaleDateString()
                      : (typeof dueDate30 === 'function' ? dueDate30(selectedInvoice) : '—')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#999' }}>Documents</Typography>
                  {selectedInvoice.invoice_document_url && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid #e0e0e0', borderRadius: 1, mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Branded Invoice (PDF)</Typography>
                      <Button size="small" variant="outlined" href={selectedInvoice.invoice_document_url} target="_blank" rel="noopener noreferrer">View</Button>
                    </Box>
                  )}
                  {selectedInvoice.invoice_attachments &&
                    (Array.isArray(selectedInvoice.invoice_attachments) ? selectedInvoice.invoice_attachments : JSON.parse(selectedInvoice.invoice_attachments || '[]')).map((att, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid #e0e0e0', borderRadius: 1, mt: 0.5 }}>
                        <Typography variant="body2">{att.name || att.originalName || `Document ${i+1}`}</Typography>
                        <Button size="small" variant="outlined" href={att.url || att.gcsUrl} target="_blank">View</Button>
                      </Box>
                    ))}
                  {!selectedInvoice.invoice_document_url &&
                    (!selectedInvoice.invoice_attachments ||
                      (Array.isArray(selectedInvoice.invoice_attachments) ? selectedInvoice.invoice_attachments : JSON.parse(selectedInvoice.invoice_attachments || '[]')).length === 0) && (
                      <Typography variant="body2" sx={{ color: '#999', mt: 0.5, fontStyle: 'italic' }}>No documents available</Typography>
                    )}
                </Grid>
              </Grid>

              <Divider sx={{ my: 3, borderBottomWidth: 2 }} />

              {/* ===== PAYMENT INFORMATION ===== */}
              <Typography variant="overline" sx={{ color: '#1a237e', fontWeight: 700, letterSpacing: 1 }}>Payment Information</Typography>
              {selectedInvoice.status === 'paid' ? (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#999' }}>Payment Method</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{selectedInvoice.payment_method || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#999' }}>Reference</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedInvoice.payment_reference || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#999' }}>Paid On</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedInvoice.paid_at && selectedInvoice.paid_at !== '1970-01-01T00:00:00.000Z'
                        ? new Date(selectedInvoice.paid_at).toLocaleDateString()
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedInvoice.attachments && selectedInvoice.attachments.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#999' }}>Payment Proof</Typography>
                      {(Array.isArray(selectedInvoice.attachments) ? selectedInvoice.attachments : JSON.parse(selectedInvoice.attachments || '[]')).map((att, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid #e0e0e0', borderRadius: 1, mt: 0.5 }}>
                          <Typography variant="body2">{att.name || att.originalName || `Attachment ${i+1}`}</Typography>
                          <Button size="small" variant="outlined" href={att.url || att.gcsUrl} target="_blank">View</Button>
                        </Box>
                      ))}
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography variant="body2" sx={{ color: '#999', mt: 1, fontStyle: 'italic' }}>Not yet paid</Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
              <Button onClick={() => setDetailOpen(false)} sx={{ borderRadius: 2 }}>Close</Button>
              {user?.role === 'homeowner' && selectedInvoice.status === 'submitted' && (
                <Button variant="contained" onClick={() => handleApprove(selectedInvoice)} disabled={loading}
                  sx={{ bgcolor: '#1976d2', borderRadius: 2, px: 3 }}>
                  {loading ? 'Processing...' : 'Approve Invoice'}
                </Button>
              )}
              {user?.role === 'homeowner' && selectedInvoice.status === 'approved' && (
                <Button variant="contained" onClick={() => { setDetailOpen(false); setPayDialog({ open: true, invoice: selectedInvoice }); }}
                  sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3 }}>
                  Mark as Paid
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog open={reminderDialog.open} onClose={() => setReminderDialog({ open: false, invoice: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>
          Send Reminder
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {reminderDialog.invoice && (
            <>
              <Box sx={{ bgcolor: '#f8f9ff', borderRadius: 2, p: 2, mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Invoice</Typography>
                <Typography sx={{ fontWeight: 700, color: '#1a237e', mt: 0.5 }}>{reminderDialog.invoice.invoice_number}</Typography>
                <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                  {reminderDialog.invoice.project_address || reminderDialog.invoice.project_name || '—'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a237e', mt: 0.5, fontWeight: 600 }}>
                  ${Number(reminderDialog.invoice.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {reminderDialog.invoice.due_date && ` · Due ${new Date(reminderDialog.invoice.due_date).toLocaleDateString()}`}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
                We'll email the homeowner a polite reminder. You'll be cc'd so they can reply directly to you.
              </Typography>
              <TextField
                label="Optional message (will appear above the reminder)"
                multiline rows={3} fullWidth
                value={reminderMsg}
                onChange={e => setReminderMsg(e.target.value)}
                placeholder="e.g. Hi John — just following up on this one. Let me know if you have any questions."
                sx={{ mb: 2 }}
              />
              {reminderError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {reminderError}
                  {reminderError.includes('opt-out') && (
                    <Box sx={{ mt: 1 }}>
                      <FormControlLabel
                        control={<Checkbox checked={reminderOverrideOptout} onChange={e => setReminderOverrideOptout(e.target.checked)} />}
                        label="Send anyway"
                      />
                    </Box>
                  )}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0', gap: 1 }}>
          <Button onClick={() => setReminderDialog({ open: false, invoice: null })} disabled={reminderSending}>Cancel</Button>
          <Button variant="contained" onClick={sendInvoiceReminder} disabled={reminderSending}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {reminderSending ? 'Sending…' : (reminderMsg.trim() ? 'Send Custom Reminder' : 'Send Standard Reminder')}
          </Button>
        </DialogActions>
      </Dialog>

            {/* Mark as Paid Dialog */}
      <Dialog open={payDialog.open} onClose={() => setPayDialog({ open: false, invoice: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Mark Invoice as Paid</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Recording payment for <strong>{payDialog.invoice?.invoice_number}</strong> — <strong>${Number(payDialog.invoice?.amount).toLocaleString()}</strong>
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Payment Method</InputLabel>
            <Select value={payForm.payment_method} onChange={e => setPayForm({...payForm, payment_method: e.target.value})}
              label="Payment Method" sx={{ borderRadius: 2 }}>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="credit_card">Credit Card</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="zelle">Zelle</MenuItem>
              <MenuItem value="venmo">Venmo</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Payment Reference / Check #" value={payForm.payment_reference}
            onChange={e => setPayForm({...payForm, payment_reference: e.target.value})}
            margin="normal" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <FileUpload onFilesChange={(files) => setPayForm({...payForm, attachments: files})} />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setPayDialog({ open: false, invoice: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleMarkPaid} disabled={loading}
            sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3 }}>
            {loading ? 'Processing...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
